import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dbData from "../../../db.json";

// ✅ Fetch all pet places
export const fetchPetPlaces = createAsyncThunk(
  "petPlaces/fetchPetPlaces",
  async () => {
    // Return mock data from db.json
    return dbData.petPlaces || {};
  }
);

// ✅ Fetch single pet place by ID (mock implementation)
export const fetchPetPlaceById = createAsyncThunk(
  "petPlaces/fetchPetPlaceById",
  async (id) => {
    // Mock implementation - find place across all categories
    const categories = dbData.petPlaces || {};
    for (const category of Object.values(categories)) {
      const place = category.find(p => p.name === id);
      if (place) return place;
    }
    throw new Error('Pet place not found');
  }
);

// ✅ Delete pet place (mock implementation)
export const deletePetPlace = createAsyncThunk(
  "petPlaces/deletePetPlace",
  async (id) => {
    // Mock delete - just return the id
    return id;
  }
);

// ✅ Update pet place (mock implementation)
export const updatePetPlace = createAsyncThunk(
  "petPlaces/updatePetPlace",
  async ({ id, data }) => {
    // Mock update - return the data with id
    return { id, ...data };
  }
);

// ✅ Create new pet place (mock implementation)
export const createPetPlace = createAsyncThunk(
  "petPlaces/createPetPlace",
  async (data) => {
    // Mock create - return the data with a generated id
    return { id: `PLACE-${Date.now()}`, ...data };
  }
);

const initialState = {
  items: [],
  detail: null,
  status: "idle",        // for list
  detailStatus: "idle",  // for single item
  error: null,
};

const petPlacesSlice = createSlice({
  name: "petPlaces",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all
      .addCase(fetchPetPlaces.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPetPlaces.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchPetPlaces.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Fetch single
      .addCase(fetchPetPlaceById.pending, (state) => {
        state.detailStatus = "loading";
        state.detail = null;
      })
      .addCase(fetchPetPlaceById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload;
      })
      .addCase(fetchPetPlaceById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete
      .addCase(deletePetPlace.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((p) => p.id !== id);
        if (state.detail?.id === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })

      // ✅ Update
      .addCase(updatePetPlace.fulfilled, (state, action) => {
        state.detail = action.payload; // refresh detail
        state.items = state.items.map((p) =>
          p.id === action.payload.id ? action.payload : p
        );
      })

      // ✅ Create
      .addCase(createPetPlace.fulfilled, (state, action) => {
        state.items.push(action.payload);
      });
  },
});

export const { clearDetail } = petPlacesSlice.actions;
export default petPlacesSlice.reducer;
